import {
  Box,
  Chip,
  ChipProps,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import { CustomCellRendererProps } from "ag-grid-react";
import { UsersListActions } from "./UsersListActions";
import {
  userRoleDescriptions,
  userRoleLabels,
} from "../../utils/constants/common";
import { User } from "@/services/cloudchipr.api";
import { capitalizeString } from "@/utils/helpers/capitalizeString";
import { UserAvatar } from "@/components/common/avatars/UserAvatar";
import { formatDate } from "@/utils/helpers/date-time/datetime-format";
import { TypographyWithTooltip } from "@/components/common/TypographyWithTooltip";
import { agGridActionColumnDefaultColumnDef } from "@/components/pages/common/ag-grid/utils/constants/agGridActionColumnDefaultColumnDef";
import { keyInsensitiveComparatorFn } from "@/components/pages/common/ag-grid/utils/helpers/sortingFunctions/keyInsensitiveComparatorFn";

export const usersTableColumnsDefs: ColDef<User>[] = [
  {
    sort: "asc",
    field: "email",
    headerName: "Email",
    minWidth: 200,
    cellRenderer: ({ data }: CustomCellRendererProps<User>) => {
      const email = data?.email;

      return <TypographyWithTooltip title={email ?? "-"} variant="body2" />;
    },
    comparator: keyInsensitiveComparatorFn,
  },
  {
    field: "name",
    headerName: "Name",
    minWidth: 200,
    cellRenderer: ({ data }: CustomCellRendererProps<User>) => {
      const name = data?.name;

      return (
        <Stack direction="row" alignItems="center" spacing={1}>
          {name && <UserAvatar profile={data?.profile} name={name} />}
          <TypographyWithTooltip title={name ?? "-"} variant="body2" />
        </Stack>
      );
    },
  },
  {
    field: "role",
    headerName: "Role",
    cellRenderer: ({ data }: CustomCellRendererProps<User>) => {
      const role = data?.role;

      if (!role) {
        return "-";
      }

      return (
        <Tooltip
          arrow
          placement="top"
          title={userRoleDescriptions.get(role) ?? ""}
        >
          <Typography
            variant="body2"
            component="span"
            sx={{
              whiteSpace: "nowrap",
              textDecoration: "underline",
              textDecorationStyle: "dashed",
              textUnderlineOffset: 5,
              textDecorationColor: grey["A400"],
            }}
          >
            {userRoleLabels.get(role) ?? "-"}
          </Typography>
        </Tooltip>
      );
    },
    valueGetter: ({ data }) => (data ? userRoleLabels.get(data.role) : "-"),
  },
  {
    field: "status",
    headerName: "Status",
    cellRenderer: ({ data }: CustomCellRendererProps<User>) => {
      const status = data?.status;

      if (!status) {
        return "-";
      }

      let statusColor: ChipProps["color"] =
        status === "pending" ? "warning" : "error";

      if (status === "active") {
        statusColor = "success";
      }

      return (
        <Chip
          size="small"
          label={capitalizeString(status)}
          variant="outlined"
          color={statusColor}
        />
      );
    },
    valueGetter: ({ data }) =>
      data?.status ? capitalizeString(data.status) : "-",
  },
  {
    field: "last_time_logged_in_at",
    headerName: "Last Login",
    cellRenderer: ({ data }: CustomCellRendererProps<User>) => {
      const lastLoginDate = data?.last_time_logged_in_at;

      if (!lastLoginDate) {
        return "-";
      }

      return (
        <TypographyWithTooltip
          title={`${formatDate(lastLoginDate, { type: "dateTime" })} (UTC)`}
          variant="body2"
        />
      );
    },
    valueGetter: ({ data }) =>
      data?.last_time_logged_in_at
        ? formatDate(data.last_time_logged_in_at, { type: "dateTime" })
        : "-",
  },
  {
    ...agGridActionColumnDefaultColumnDef,
    width: 60,
    minWidth: 60,
    maxWidth: 60,
    colId: "actions",
    cellDataType: false,
    cellRenderer: (params: ICellRendererParams<User>) => {
      const user = params.data;

      if (user?.role !== "owner" && user?.id && user?.email && user?.role) {
        return (
          <UsersListActions
            userId={user.id}
            email={user.email}
            status={user.status}
            role={user.role}
          />
        );
      }

      return <Box pt={5} />;
    },
  } as ColDef<User>,
];
